'use client';

import { useMemo, useState } from 'react';
import { computeAmortization } from '@/lib/browser-healthfin-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserAmortizationCalculator() {
  const [amount, setAmount] = useState('250000');
  const [rate, setRate] = useState('6');
  const [years, setYears] = useState('30');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const result = useMemo(() => computeAmortization(amount, rate, years), [amount, rate, years]);

  async function handleCopyCsv() {
    if (!result.ok || !result.schedule) return;
    const header = 'Payment,Amount,Principal,Interest,Balance';
    // Strip thousands separators so each formatted amount is a single CSV cell.
    const clean = (value: string) => value.replace(/,/g, '');
    const rows = result.schedule.map(
      (row) =>
        `${row.period},${clean(row.payment)},${clean(row.principal)},${clean(row.interest)},${clean(
          row.balance,
        )}`,
    );
    const csv = [header, ...rows].join('\n');
    try {
      await copyText(csv);
      setCopyMessage('Schedule copied as CSV.');
    } catch {
      setCopyMessage('The browser could not copy the schedule.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Amortization</span>
        <h2 className="tool-live-title">Loan Amortization Calculator</h2>
        <p className="tool-live-subtitle">
          Enter a loan amount, interest rate, and term to see the monthly payment and a full
          payment-by-payment schedule. Everything is calculated locally in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Loan amount</span>
              <input
                inputMode="decimal"
                onChange={(event) => setAmount(event.target.value)}
                placeholder="250000"
                type="text"
                value={amount}
              />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Annual interest rate (%)</span>
              <input
                inputMode="decimal"
                onChange={(event) => setRate(event.target.value)}
                placeholder="6"
                type="text"
                value={rate}
              />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Loan term (years)</span>
              <input
                inputMode="decimal"
                onChange={(event) => setYears(event.target.value)}
                placeholder="30"
                type="text"
                value={years}
              />
            </label>
          </div>
          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              disabled={!result.ok}
              onClick={handleCopyCsv}
              type="button"
            >
              Copy schedule (CSV)
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
          <p className="tool-live-panel-note">
            Amounts are shown without a currency symbol so they work anywhere. This is an estimate;
            your lender may round differently.
          </p>
        </section>

        <section className="tool-live-preview">
          {!result.ok ? (
            <p className="status-message" style={{ color: '#b91c1c' }}>
              {result.message}
            </p>
          ) : (
            <>
              <div className="tool-html-meta-grid">
                <div className="tool-live-field">
                  <span>Monthly payment</span>
                  <input readOnly type="text" value={result.monthlyPayment ?? ''} />
                </div>
                <div className="tool-live-field">
                  <span>Number of payments</span>
                  <input readOnly type="text" value={String(result.numberOfPayments ?? '')} />
                </div>
                <div className="tool-live-field">
                  <span>Total interest</span>
                  <input readOnly type="text" value={result.totalInterest ?? ''} />
                </div>
                <div className="tool-live-field">
                  <span>Total paid</span>
                  <input readOnly type="text" value={result.totalPaid ?? ''} />
                </div>
              </div>

              <div
                style={{
                  marginTop: '1rem',
                  maxHeight: '360px',
                  overflow: 'auto',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                  borderRadius: '10px',
                }}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.82rem',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  <thead>
                    <tr>
                      {['#', 'Payment', 'Principal', 'Interest', 'Balance'].map((label) => (
                        <th
                          key={label}
                          style={{
                            position: 'sticky',
                            top: 0,
                            background: 'rgba(100, 116, 139, 0.18)',
                            backdropFilter: 'blur(4px)',
                            textAlign: label === '#' ? 'left' : 'right',
                            padding: '0.45rem 0.6rem',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(result.schedule ?? []).map((row) => (
                      <tr key={row.period}>
                        <td
                          style={{
                            padding: '0.35rem 0.6rem',
                            borderTop: '1px solid rgba(148, 163, 184, 0.2)',
                            textAlign: 'left',
                            opacity: 0.7,
                          }}
                        >
                          {row.period}
                        </td>
                        <td style={cellStyle}>{row.payment}</td>
                        <td style={cellStyle}>{row.principal}</td>
                        <td style={cellStyle}>{row.interest}</td>
                        <td style={cellStyle}>{row.balance}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="tool-live-panel-note">
                Scroll the table to see every payment. Early payments are mostly interest; later
                payments are mostly principal.
              </p>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

const cellStyle: React.CSSProperties = {
  padding: '0.35rem 0.6rem',
  borderTop: '1px solid rgba(148, 163, 184, 0.2)',
  textAlign: 'right',
  whiteSpace: 'nowrap',
};
